import { useEffect, useState } from 'react';
import he from 'he';

interface WordPressRenderedField {
  rendered?: string;
}

interface WordPressWhitepaper {
  id: number;
  date: string;
  link: string;
  slug: string;
  title?: WordPressRenderedField;
  excerpt?: WordPressRenderedField;
  content?: WordPressRenderedField;
  acf?: {
    pdf_file?: string;
    short_description?: string;
    author_name?: string;
  };
  meta?: {
    pdf_url?: string;
  };
  pdf_url?: string;
  whitepaper_pdf_url?: string;
}

export interface WhitepaperCardItem {
  id: number;
  slug: string;
  title: string;
  description: string;
  link: string;
  date: string;
  pdfUrl: string | null;
}

const WHITEPAPERS_ENDPOINTS = [
  'https://blog.medbioinformatics.com/wp-json/wp/v2/whitepapers',
  'https://blog.medbioinformatics.com/wp-json/wp/v2/whitepaper',
];

const stripHtml = (value: string) => value.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
const extractPdfUrl = (html: string) => {
  const match = html.match(/https:\/\/[^\s"'<>]+\.pdf(?:\?[^\s"'<>]*)?/i);
  return match?.[0] || null;
};
const getValidHttpsUrl = (value?: string | null) => {
  if (!value) return null;
  try {
    const parsed = new URL(value);
    if (parsed.protocol !== 'https:') return null;
    if (!parsed.hostname.includes('.')) return null;
    return parsed.toString();
  } catch {
    return null;
  }
};

const formatDate = (value: string) =>
  new Date(value).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

export const useWhitepapers = () => {
  const [whitepapers, setWhitepapers] = useState<WhitepaperCardItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchWhitepapers = async () => {
      let data: WordPressWhitepaper[] | null = null;

      for (const endpoint of WHITEPAPERS_ENDPOINTS) {
        try {
          const response = await fetch(endpoint);
          if (!response.ok) {
            continue;
          }
          const parsed = await response.json();
          if (Array.isArray(parsed)) {
            data = parsed;
            break;
          }
        } catch {
          // Try next endpoint
        }
      }

      if (!data) {
        throw new Error('Whitepapers request failed for all configured endpoints');
      }

      {
        const mappedWhitepapers = data.map(item => {
          const decodedTitle = he.decode(item.title?.rendered || 'Untitled whitepaper');
          const rawDescription =
            item.acf?.short_description || item.excerpt?.rendered || item.content?.rendered || '';
          const decodedDescription = he.decode(stripHtml(rawDescription));
          const shortDescription =
            decodedDescription.length > 200 ? `${decodedDescription.slice(0, 200)}...` : decodedDescription;
          const explicitPdfUrl =
            getValidHttpsUrl(item.whitepaper_pdf_url) ||
            getValidHttpsUrl(item.pdf_url) ||
            getValidHttpsUrl(item.acf?.pdf_file) ||
            getValidHttpsUrl(item.meta?.pdf_url);
          const pdfUrl = explicitPdfUrl || extractPdfUrl(item.content?.rendered || '');

          return {
            id: item.id,
            slug: item.slug,
            title: decodedTitle,
            description: shortDescription,
            link: item.link,
            date: formatDate(item.date),
            pdfUrl,
          };
        });

        setWhitepapers(mappedWhitepapers);
        setLoading(false);
      }
    };

    fetchWhitepapers().catch(fetchError => {
        setError(fetchError?.message || 'Failed to load whitepapers');
        setLoading(false);
      });
  }, []);

  return { whitepapers, loading, error };
};
